import {
  Button,
  ProgressBar,
  StatusMessage,
  Text,
} from '@inmotionnow/momentum-components-react'
import { StatusCodes } from 'http-status-codes'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { downloadFailureReport } from '@/features/bulk-import/bulk-import-report'
import {
  useDispatchBulkImportRunMutation,
  useGetBulkImportProgressQuery,
  useGetBulkImportRowsQuery,
  useRetryBulkImportFailuresMutation,
  useRetryBulkImportRowMutation,
} from '@/features/bulk-import/bulkImportApi'
import { BulkImportRecoverPanel } from '@/features/bulk-import/BulkImportRecoverPanel'
import { BulkImportRowList } from '@/features/bulk-import/BulkImportRowList'
import { logger } from '@/utils/logger'

/** How often an unfinished run is re-read. Stops the moment it finishes. */
const POLL_INTERVAL_MS = 3000

/**
 * How long to keep watching a finished run after asking for something that a worker
 * will carry out. Long enough for a queued batch retry to reach a row, short enough
 * that a retry which moved nothing stops polling on its own.
 */
const WATCH_AFTER_ACTION_MS = 60000

const NOTHING_TO_DISPATCH = 'nothing_to_dispatch'
const DISPATCH_NOT_QUEUED = 'dispatch_not_queued'
const ROW_NOT_RETRYABLE = 'row_not_retryable'
const RETRY_NOT_QUEUED = 'retry_not_queued'

/**
 * Where the run view's messages go. Each receives a message already translated, and may
 * be async; a rejection is logged like a throw.
 */
export interface BulkImportRunNotifier {
  success: (message: string) => void | Promise<void>
  info: (message: string) => void | Promise<void>
  warning: (message: string) => void | Promise<void>
  error: (message: string) => void | Promise<void>
}

const toastNotifier: BulkImportRunNotifier = {
  success: message => {
    toast.success(message)
  },
  info: message => {
    toast.info(message)
  },
  warning: message => {
    toast.warning(message)
  },
  error: message => {
    toast.error(message)
  },
}

export interface BulkImportRunPanelProps {
  /** The run to show. Undefined when the caller could not read a valid id. */
  runId: number | undefined
  /** Receives the view's messages. Defaults to toasts. */
  notifier?: BulkImportRunNotifier
}

/**
 * Why one import could not be read.
 *
 * Only a 404 means the import is not there. A transient gateway failure or a token
 * refresh losing the race — both likely on the very first read, which happens moments
 * after the upload navigates here — would otherwise tell an admin that the import they
 * just started does not exist, which is the worst available reading of a run that is
 * in fact provisioning. A 403 is its own answer: a team member following a shared link,
 * or an admin of a different account, is not looking at something missing.
 */
function loadFailureKey(error: unknown): string {
  const status = readStatus(error)
  if (status === StatusCodes.NOT_FOUND) return 'BULK_IMPORT_RUN_NOT_FOUND'
  if (status === StatusCodes.FORBIDDEN) return 'BULK_IMPORT_RUN_FORBIDDEN'
  return 'BULK_IMPORT_RUN_LOAD_FAILED'
}

/**
 * Whether a failed read will still be failing on the next poll.
 *
 * Only the two answers that are about this run rather than about the moment: it is gone,
 * or it is not yours. Everything else — a gateway blip, a token refresh losing a race —
 * is worth asking again.
 */
function isTerminalFailure(error: unknown): boolean {
  const status = readStatus(error)
  return status === StatusCodes.NOT_FOUND || status === StatusCodes.FORBIDDEN
}

function readStatus(error: unknown): unknown {
  return typeof error === 'object' && error !== null && 'status' in error
    ? (error as { status: unknown }).status
    : undefined
}

function readCode(error: unknown): string | undefined {
  if (typeof error !== 'object' || error === null || !('data' in error)) {
    return undefined
  }
  const { data } = error as { data: unknown }
  if (typeof data !== 'object' || data === null) return undefined
  const code = (data as { extra?: { code?: unknown } }).extra?.code
  return typeof code === 'string' ? code : undefined
}

/**
 * One import, while it is happening.
 *
 * Every number shown is the backend's, counted from the rows at the moment of asking.
 * Nothing is cached, and nothing is recomputed from the row list — a tally kept in step
 * with reality by hand is the defect this whole feature was designed against (DM-5917).
 * That is also why polling re-reads rather than patching what it already had.
 *
 * Renders without page chrome, so a host can place it in its own frame.
 */
export const BulkImportRunPanel = ({
  runId,
  notifier = toastNotifier,
}: BulkImportRunPanelProps): React.ReactElement => (
  // Keyed by the run so a host switching runs never carries one run's in-flight
  // retries, watch window or last-read data into the next.
  <BulkImportRunContent key={runId} runId={runId} notifier={notifier} />
)

const BulkImportRunContent = ({
  runId: parsedRunId,
  notifier,
}: {
  runId: number | undefined
  notifier: BulkImportRunNotifier
}): React.ReactElement => {
  const { t } = useTranslation()
  const [isDownloading, setIsDownloading] = React.useState(false)

  const notify = (
    level: keyof BulkImportRunNotifier,
    messageKey: string,
  ): void => {
    // A host that can't take the message must not break the action that raised it.
    const logNotifyFailure = (notifyError: unknown): void => {
      logger.error('Bulk import run message could not be shown:', notifyError)
    }
    try {
      void Promise.resolve(notifier[level](t(messageKey))).catch(
        logNotifyFailure,
      )
    } catch (notifyError) {
      logNotifyFailure(notifyError)
    }
  }

  const skip = parsedRunId === undefined

  // Polling stops the moment the backend says the run is finished, rather than when the
  // counts happen to look done — `is_complete` is its answer to exactly that question.
  // The interval is read back from the data the same hook returns, which RTK Query
  // accepts: a changing interval reschedules the existing subscription.
  const [pollingInterval, setPollingInterval] = React.useState(POLL_INTERVAL_MS)
  // A batch retry is normally asked for on a *finished* run, where polling has already
  // stopped — and it is carried out by a worker, so the refetch that follows the 202
  // still sees a finished run and the gate below switches polling straight back off.
  // The screen would then sit unchanged behind a message saying "watch them below". This
  // keeps it watching until a poll sees the run moving again, bounded so a retry that
  // genuinely changed nothing does not poll for the life of the tab.
  const [watchUntil, setWatchUntil] = React.useState(0)

  const {
    data: progress,
    isLoading,
    isError,
    error,
  } = useGetBulkImportProgressQuery(parsedRunId as number, {
    skip,
    pollingInterval,
  })
  const {
    data: rows,
    isError: isRowsError,
    refetch: refetchRows,
  } = useGetBulkImportRowsQuery(parsedRunId as number, {
    skip,
    pollingInterval,
  })

  React.useEffect(() => {
    if (progress) {
      const stillWatching = Date.now() < watchUntil
      setPollingInterval(
        progress.is_complete && !stillWatching ? 0 : POLL_INTERVAL_MS,
      )
      // Movement seen: the ordinary gate takes over again.
      if (!progress.is_complete) setWatchUntil(0)
      return
    }
    // No progress means the read failed, and polling has to stop for the failures that
    // will never resolve themselves — otherwise a bookmarked link to a deleted run sits
    // on a terminal "not found" message asking for it forty times a minute for as long
    // as the tab is open, and a 403 does the same to the backend's auth logs. Transient
    // failures keep retrying, which is the whole point of telling them apart.
    setPollingInterval(isTerminalFailure(error) ? 0 : POLL_INTERVAL_MS)
  }, [progress, error, watchUntil])

  // The two queries poll independently, so the rows response from the tick that ends the
  // run may have been served just before it completed. Polling then stops and the list
  // rests permanently one snapshot stale — showing rows as still being worked on for a
  // run that has finished.
  const isComplete = progress?.is_complete ?? false
  React.useEffect(() => {
    if (isComplete && !skip) refetchRows()
  }, [isComplete, skip, refetchRows])

  const [dispatchRun, { isLoading: isDispatching }] =
    useDispatchBulkImportRunMutation()
  const [retryRow] = useRetryBulkImportRowMutation()
  const [retryFailures, { isLoading: isRetryingAll }] =
    useRetryBulkImportFailuresMutation()
  // A set, not one id: clicking a second row while the first is in flight would
  // otherwise clear the first one's spinner and re-enable its button, and a second POST
  // for a row already being retried spends another of that row's three attempts.
  const [retryingRowIds, setRetryingRowIds] = React.useState<
    ReadonlySet<number>
  >(() => new Set())

  const failedRowCount = rows
    ? rows.filter(r => r.status === 'error').length
    : 0

  const handleRetryRow = async (rowId: number): Promise<void> => {
    if (parsedRunId === undefined) return
    if (retryingRowIds.has(rowId)) return
    setRetryingRowIds(prev => new Set(prev).add(rowId))
    try {
      const row = await retryRow({ runId: parsedRunId, rowId }).unwrap()
      // The retry cap lives in the backend and is spent inside this call, so a row that
      // had attempts left when the list was drawn can come back handed to support
      // instead of retried. It answers 200 either way — the row's new status is what
      // tells them apart, and calling that "retrying" would be a plain falsehood.
      if (row.status === 'needs_support') {
        notify('info', 'BULK_IMPORT_ROW_ESCALATED')
      } else {
        notify('success', 'BULK_IMPORT_ROW_RETRIED')
      }
    } catch (retryError) {
      if (readCode(retryError) === ROW_NOT_RETRYABLE) {
        // The list was true when it was drawn and a colleague has since retried this
        // row. That is an ordinary outcome, not a failure — and the invalidation above
        // has already refetched, so the admin is looking at the truth by the time they
        // read this.
        notify('info', 'BULK_IMPORT_ROW_NOT_RETRYABLE')
      } else {
        notify('error', 'BULK_IMPORT_ROW_RETRY_FAILED')
      }
      logger.error('Error retrying bulk import row:', retryError)
    } finally {
      setRetryingRowIds(prev => {
        const next = new Set(prev)
        next.delete(rowId)
        return next
      })
    }
  }

  const handleRetryAll = async (): Promise<void> => {
    if (parsedRunId === undefined) return
    try {
      await retryFailures(parsedRunId).unwrap()
      // 202 and the run id. There is nothing to report yet, and no results panel to
      // build: what each retry did appears in the polling — which has to be restarted,
      // because this is normally asked for on a run that had already finished.
      setWatchUntil(Date.now() + WATCH_AFTER_ACTION_MS)
      notify('success', 'BULK_IMPORT_RETRY_ALL_REQUESTED')
    } catch (retryError) {
      if (readCode(retryError) === RETRY_NOT_QUEUED) {
        // Deliberately not "try again". Landing here proves the publish was not
        // confirmed, not that it did not happen — so a second pass could be started
        // over a run already retrying, and a fast-failing run can spend a row's
        // remaining attempt on it (KPS-610).
        notify('warning', 'BULK_IMPORT_RETRY_NOT_QUEUED')
      } else {
        notify('error', 'BULK_IMPORT_RETRY_ALL_FAILED')
      }
      logger.error('Error retrying bulk import failures:', retryError)
    }
  }

  const handleDispatch = async (): Promise<void> => {
    if (parsedRunId === undefined) return
    try {
      await dispatchRun(parsedRunId).unwrap()
      // 202 only. The rows moving is the confirmation, and this screen is watching.
      notify('success', 'BULK_IMPORT_DISPATCH_REQUESTED')
    } catch (dispatchError) {
      const code = readCode(dispatchError)
      if (code === NOTHING_TO_DISPATCH) {
        // Not a failure the admin caused, and the backend deliberately names no next
        // action — a run with nothing waiting may have succeeded, may have failures to
        // retry, or may have failures that have spent their attempts.
        notify('info', 'BULK_IMPORT_NOTHING_TO_DISPATCH')
      } else if (code === DISPATCH_NOT_QUEUED) {
        notify('error', 'BULK_IMPORT_DISPATCH_NOT_QUEUED')
        logger.error('Error dispatching bulk import run:', dispatchError)
      } else {
        notify('error', 'BULK_IMPORT_DISPATCH_FAILED')
        logger.error('Error dispatching bulk import run:', dispatchError)
      }
    }
  }

  const handleDownload = async (): Promise<void> => {
    if (parsedRunId === undefined) return
    setIsDownloading(true)
    try {
      await downloadFailureReport(parsedRunId)
    } catch (downloadError) {
      notify('error', 'BULK_IMPORT_REPORT_DOWNLOAD_FAILED')
      logger.error('Error downloading failure report:', downloadError)
    } finally {
      setIsDownloading(false)
    }
  }

  if (skip) return <>{t('BULK_IMPORT_RUN_NOT_FOUND')}</>
  if (isLoading) return <>{t('LOADING')}</>
  if (isError || !progress) {
    return (
      <Text tag='p' dataTestingId='bulk-import-run-error'>
        {t(loadFailureKey(error))}
      </Text>
    )
  }

  // Only these two are the admin's to act on; the rest are moving on their own.
  const needsAttention = (rows ?? []).filter(
    r => r.status === 'error' || r.status === 'needs_support',
  )
  const stillWorking = (rows ?? []).filter(
    r => r.status !== 'error' && r.status !== 'needs_support',
  )

  // A batch retry has been asked for and no poll has yet seen the run move. Offering
  // the button again here is what lets a second pass start over rows already being
  // retried — which, on a fast-failing run, spends another of a row's three attempts.
  const awaitingWorker = progress.is_complete && Date.now() < watchUntil

  const needsReupload = progress.status === 'needs_reupload'
  // The backend holds a run at needs_reupload until nothing is unresolved, so that a
  // retry moving a row through PROCESSING cannot flicker the status back and forth. That
  // leaves one window where it says a corrected file is wanted while no row has failed:
  // the rows from the last one are still being created. Saying "download the failure
  // report" there points at a report with nothing in it, and at a button this page
  // correctly stops offering — so the message says what is actually happening instead.
  const correctionsInFlight = needsReupload && progress.failed_count === 0
  // Resolved, not created. A run that finishes 92 created and 8 failed is finished, and
  // a bar stuck at 92% beside a status reading "Finished" says otherwise; the warning
  // theme is what carries the failures.
  const percent = progress.total_rows
    ? Math.round(
        ((progress.created_count + progress.failed_count) /
          progress.total_rows) *
          100,
      )
    : 0

  return (
    <div data-testid='bulk-import-run'>
      {/* A run past the re-upload threshold reads differently on purpose: per-row retry
          is no longer the recommended path, and the admin needs to know that before
          working through the list one row at a time. */}
      {needsReupload && (
        <StatusMessage
          theme='warning'
          show
          title={
            correctionsInFlight
              ? t('BULK_IMPORT_REUPLOAD_PROCESSING_TITLE')
              : t('BULK_IMPORT_NEEDS_REUPLOAD_TITLE')
          }
          content={
            correctionsInFlight
              ? t('BULK_IMPORT_REUPLOAD_PROCESSING_HELP')
              : t('BULK_IMPORT_NEEDS_REUPLOAD_HELP')
          }
        />
      )}
      {/* Withdrawn while the last corrected file's rows are still being created, along
          with the download button and the instruction above. Its own help text opens by
          telling the admin to download the failure report using the button below, so
          leaving it up would restore in the panel exactly the contradiction the message
          above just removed. There is also nothing useful to do in that window: the rows
          they sent are mid-flight, and a second file against the same run only re-sends
          rows the backend already counts as applied. It comes back by itself if anything
          in the batch fails. */}
      {needsReupload && !correctionsInFlight && parsedRunId !== undefined && (
        // Keyed by the run: navigating straight from one run needing a corrected file to
        // another would otherwise keep the first one's file selected and send it against
        // the second, which is exactly the "a correction belongs to its own run" rule
        // this panel exists to hold.
        <BulkImportRecoverPanel key={parsedRunId} runId={parsedRunId} />
      )}

      <Text tag='p' dataTestingId='bulk-import-run-status'>
        {`${t('STATUS')}: ${t(
          `BULK_IMPORT_STATUS_${progress.status.toUpperCase()}`,
        )}`}
      </Text>

      <ProgressBar
        percent={percent}
        theme={progress.failed_count ? 'warning' : 'info'}
        ariaLabel={t('BULK_IMPORT_CREATED')}
      />

      <Text tag='p' dataTestingId='bulk-import-created-count'>
        {`${t('BULK_IMPORT_CREATED')}: ${progress.created_count} / ${
          progress.total_rows
        }`}
      </Text>
      {/* Failed and in-progress stay separate. A row still provisioning needs nothing
          from anyone, and one number covering both tells an admin several things are
          wrong when one is. */}
      <Text tag='p' dataTestingId='bulk-import-failed-count'>
        {`${t('BULK_IMPORT_FAILED')}: ${progress.failed_count}`}
      </Text>
      <Text tag='p' dataTestingId='bulk-import-in-progress-count'>
        {`${t('BULK_IMPORT_IN_PROGRESS')}: ${progress.in_progress_count}`}
      </Text>

      {/* No success summary while anything is still unresolved, whatever the counts
          look like. */}
      {progress.is_complete && progress.failed_count === 0 && (
        <Text tag='p' dataTestingId='bulk-import-complete'>
          {t('BULK_IMPORT_COMPLETE')}
        </Text>
      )}

      <div style={{ display: 'flex', gap: '1rem', margin: '1rem 0' }}>
        {/* Offered whenever there is something in it, which is not the same as whenever
            the run is unfinished: at scale an admin wants the failures they already have,
            so this does not wait for the end — but a run that recovers fully ends with
            nothing to report, and offering a file that comes back as a header row and no
            rows reads as though the failures were lost rather than fixed (KPS-614 AC1).

            Gated on the backend's failed_count rather than the error rows counted for the
            retry buttons below: those are the rows a retry can still act on, while the
            report also carries the ones handed to support, which have none left. A run
            ending with only those has an empty retry list and a report with content. */}
        {progress.failed_count > 0 && (
          <Button
            label={t('BULK_IMPORT_DOWNLOAD_REPORT')}
            theme='flat'
            loading={isDownloading}
            dataTestingId='bulk-import-download-report'
            onClick={handleDownload}
          />
        )}
        {failedRowCount > 0 && !awaitingWorker && (
          <Button
            label={t('BULK_IMPORT_RETRY_ALL')}
            theme='flat'
            loading={isRetryingAll}
            dataTestingId='bulk-import-retry-all'
            onClick={handleRetryAll}
          />
        )}
        {/* Offered on any unfinished run rather than only one that looks stuck: for its
            first seconds a healthy run is indistinguishable from a stranded one, which
            is why the backend refuses to gate on the question either. */}
        {!progress.is_complete && (
          <Button
            label={t('BULK_IMPORT_DISPATCH')}
            theme='flat'
            loading={isDispatching}
            dataTestingId='bulk-import-dispatch'
            onClick={handleDispatch}
          />
        )}
      </div>

      {/* The endpoint returns everything still outstanding — the failures *and* the rows
          still being worked on. Both belong on screen, but they are split here for the
          same reason the counts above are: heading five hundred rows that are merely
          provisioning with "needs your attention" is exactly the conflation those
          separate numbers exist to prevent. */}
      {needsAttention.length > 0 && (
        <div data-testid='bulk-import-needs-attention'>
          <Text tag='p' textWeight='bold'>
            {t('BULK_IMPORT_NEEDS_ATTENTION')}
          </Text>
          {/* Said where the rows are, not beside the button, because the fear it
              answers — creating somebody a second time — is felt while looking at the
              list of people. */}
          {failedRowCount > 0 && (
            <Text tag='p' size='sm' dataTestingId='bulk-import-retry-scope'>
              {t('BULK_IMPORT_RETRY_SCOPE')}
            </Text>
          )}
          <BulkImportRowList
            rows={needsAttention}
            onRetryRow={handleRetryRow}
            retryingRowIds={retryingRowIds}
          />
        </div>
      )}
      {stillWorking.length > 0 && (
        <div data-testid='bulk-import-still-working'>
          <Text tag='p' textWeight='bold'>
            {t('BULK_IMPORT_STILL_IN_PROGRESS')}
          </Text>
          {/* No retry offered here: a row still being worked on has nothing to retry. */}
          <BulkImportRowList rows={stillWorking} />
        </div>
      )}
      {/* "Nothing to show" and "we could not fetch what to show" are different answers,
          and the admin is here to find out which rows to fix. */}
      {isRowsError && (
        <Text tag='p' dataTestingId='bulk-import-rows-unavailable'>
          {t('BULK_IMPORT_ROWS_UNAVAILABLE')}
        </Text>
      )}
      {rows && rows.length === 0 && progress.is_complete && (
        <Text tag='p' dataTestingId='bulk-import-nothing-outstanding'>
          {t('BULK_IMPORT_NOTHING_OUTSTANDING')}
        </Text>
      )}
    </div>
  )
}
